import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: BACKEND_URL ? `${BACKEND_URL}/api` : '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      const expiry = localStorage.getItem('token_expiry');
      if (expiry && Date.now() > Number(expiry)) {
        localStorage.removeItem('token');
        localStorage.removeItem('token_expiry');
        const pathname = window.location.pathname;
        const noRedirectPaths = ['/auth', '/reset-password'];
        if (!noRedirectPaths.some(p => pathname.startsWith(p))) {
          window.location.href = '/auth';
        }
        return Promise.reject(new Error('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('token_expiry');
      // Don't redirect on auth-related pages or reset-password page
      const pathname = window.location.pathname;
      const noRedirectPaths = ['/auth', '/reset-password'];
      if (!noRedirectPaths.some(p => pathname.startsWith(p))) {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

export const itineraryApi = {
  create: (tripInput) => api.post('/itineraries', { trip_input: tripInput }, { timeout: 120000 }),
  generate: (id) => api.post(`/itineraries/${id}/generate`, {}, { timeout: 120000 }),
  generateDirect: (tripInput) => api.post('/generate-itinerary', { trip_input: tripInput }, { timeout: 120000 }),
  list: () => api.get('/itineraries'),
  get: (id) => api.get(`/itineraries/${id}`),
  delete: (id) => api.delete(`/itineraries/${id}`),
  save: (id, itinerary) => api.patch(`/itineraries/${id}`, { itinerary, status: 'completed' }),
  updateTripInput: (id, tripInput) => api.put(`/itineraries/${id}/trip-input`, tripInput),
  shareEmail: (itineraryId, recipientEmails, personalMessage) =>
    api.post('/share-email', { itinerary_id: itineraryId, recipient_emails: recipientEmails, personal_message: personalMessage }),
  generatePackingList: (params) => api.post('/generate-packing-list', params),
  getVisaInfo: (nationality, destination) => api.get(`/visa-info?nationality=${nationality}&destination=${destination}`),
  // Expenses
  addExpense: (itineraryId, expense) => api.post(`/itineraries/${itineraryId}/expenses`, expense),
  listExpenses: (itineraryId) => api.get(`/itineraries/${itineraryId}/expenses`),
  deleteExpense: (expenseId) => api.delete(`/expenses/${expenseId}`),
  getBudgetSummary: (itineraryId) => api.get(`/itineraries/${itineraryId}/budget-summary`),
  getBudgetComparison: (itineraryId) => api.get(`/itineraries/${itineraryId}/budget-comparison`),
  // Companions
  inviteCompanion: (itineraryId, data) => api.post(`/itineraries/${itineraryId}/companions`, data),
  listCompanions: (itineraryId) => api.get(`/itineraries/${itineraryId}/companions`),
  removeCompanion: (companionId) => api.delete(`/companions/${companionId}`),
  respondToCompanion: (companionId, action) => api.put(`/companions/${companionId}/respond`, { action }),
  // Weather
  getWeatherForecast: (city, country, startDate, endDate) => {
    let url = `/weather-forecast?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}`;
    if (startDate) url += `&start_date=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&end_date=${encodeURIComponent(endDate)}`;
    return api.get(url);
  },
  // Insurance
  getTravelInsurance: (destination, duration, travelers) => api.get(`/travel-insurance?destination=${encodeURIComponent(destination)}&duration=${duration}&travelers=${travelers}`),
  // Compare
  compareDestinations: (data) => api.post('/compare-destinations', data),
  // Profile
  updateProfile: (data) => api.put('/auth/profile', data),
  // Currency
  convertCurrency: (amount, from, to) => api.get(`/currency-convert?amount=${amount}&from_currency=${from}&to_currency=${to}`),
  // Push Notifications
  getVapidKey: () => api.get('/push/vapid-public-key'),
  pushSubscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  pushUnsubscribe: (subscription) => api.post('/push/unsubscribe', { subscription }),
  pushStatus: () => api.get('/push/status'),
  // Social Sharing
  getShareCard: (itineraryId) => api.get(`/itineraries/${itineraryId}/share-card`),
  getBudgetShareCard: (itineraryId) => api.get(`/itineraries/${itineraryId}/budget-share-card`),
  // Trip Reminders
  checkReminders: () => api.get('/reminders/check'),
  // Flights
  searchFlights: (params) => api.get('/flights/search', { params }),
  getPopularRoutes: () => api.get('/flights/popular-routes'),
  // Hotels
  getBookingLink: (params) => api.get('/hotels/booking-link', { params }),
  getPopularDestinations: () => api.get('/hotels/popular-destinations'),
  // Templates
  getTemplates: (params) => api.get('/templates', { params }),
  getTemplate: (id) => api.get(`/templates/${id}`),
  useTemplate: (id) => api.post(`/templates/${id}/use`),
  publishTemplate: (data) => api.post('/templates/publish', data),
  // Timeline
  getTimeline: (itineraryId) => api.get(`/itineraries/${itineraryId}/timeline`),
  createTimelineShare: (itineraryId) => api.post(`/itineraries/${itineraryId}/timeline/share`),
  getSharedTimeline: (shareId) => api.get(`/timeline/shared/${shareId}`),
  getTimelineNotes: (itineraryId) => api.get(`/itineraries/${itineraryId}/timeline/notes`),
  saveTimelineNotes: (itineraryId, notes, activityEdits) => api.put(`/itineraries/${itineraryId}/timeline/notes`, { notes, activity_edits: activityEdits }),
  // Blog
  getBlog: (itineraryId) => api.get(`/itineraries/${itineraryId}/blog`),
  generateBlog: (itineraryId) => api.post(`/itineraries/${itineraryId}/blog/generate`),
  saveBlog: (itineraryId, data) => api.put(`/itineraries/${itineraryId}/blog`, data),
  createBlogShare: (itineraryId) => api.post(`/itineraries/${itineraryId}/blog/share`),
  getSharedBlog: (shareId) => api.get(`/blog/shared/${shareId}`),
  generateBlogCover: (itineraryId) => api.post(`/itineraries/${itineraryId}/blog/cover`),
  // Story & Blog listings
  listStories: () => api.get('/timeline/my-stories'),
  deleteTimeline: (itineraryId) => api.delete(`/itineraries/${itineraryId}/timeline`),
  listBlogs: () => api.get('/blog/my-blogs'),
  deleteBlog: (itineraryId) => api.delete(`/itineraries/${itineraryId}/blog`),
  // Admin
  getAdminStats: () => api.get('/admin/stats'),
  getAdminUsers: (params) => api.get('/admin/users', { params }),
  getAdminTrends: (days) => api.get(`/admin/trends?days=${days || 30}`),
  getAdminRecentActivity: () => api.get('/admin/recent-activity'),
  addAdminUser: (data) => api.post('/admin/users', data),
  // Revenue
  getRevenue: () => api.get('/admin/revenue'),
  addRevenue: (data) => api.post('/admin/revenue', data),
  updateRevenue: (id, data) => api.put(`/admin/revenue/${id}`, data),
  deleteRevenue: (id) => api.delete(`/admin/revenue/${id}`),
  // Nearby Places
  getNearbyPlaces: (location, city, country) => api.get('/nearby-places', { params: { location, city, country } }),
  // Budget listing
  listMyBudgets: () => api.get('/budget/my-budgets'),
  deleteBudgetExpenses: (itineraryId) => api.delete(`/budget/${itineraryId}`),
};

export default api;
